import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@ui/components/ui/button';
import { Checkbox } from '@ui/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';

interface ProductLevelsDialogProps {
  open: boolean;
  title: string;
  /** What changes on the profile, and from when the ticked products follow it. */
  body: string;
  products: { id: string; name: string }[];
  isLoading: boolean;
  isPending: boolean;
  onCancel: () => void;
  /** Saves the profile and applies the level to the products picked, which may be none. */
  onConfirm: (productIds: string[]) => void;
}

/** A product keeps the level and chapter it copied in, so a new one only reaches the products picked here. */
const ProductLevelsDialog = ({
  open,
  title,
  body,
  products,
  isLoading,
  isPending,
  onCancel,
  onConfirm,
}: ProductLevelsDialogProps): ReactElement => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string[]>([]);

  // Every product starts ticked when the dialog opens; keyed on the sorted ids, so a refetch keeps what was unticked.
  const ids = products
    .map(product => product.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (open) setPicked(ids ? ids.split(',') : []);
  }, [open, ids]);

  const toggle = (productId: string, checked: boolean): void =>
    setPicked(prev => (checked ? [...prev, productId] : prev.filter(id => id !== productId)));

  const allPicked = products.length > 0 && picked.length === products.length;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{body}</p>

        {/* Boxed like a dropdown's list, so it reads as the whole set of their products. */}
        <div className="overflow-hidden rounded-lg bg-popover ring-1 ring-foreground/10">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('memberDetails.productLevels.listLabel', { count: products.length })}
            </span>
            {products.length ? (
              <button
                type="button"
                onClick={() => setPicked(allPicked ? [] : products.map(product => product.id))}
                disabled={isPending}
                className="cursor-pointer text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                {allPicked ? t('memberDetails.productLevels.selectNone') : t('memberDetails.productLevels.selectAll')}
              </button>
            ) : null}
          </div>
          {isLoading ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">{t('memberDetails.productLevels.loading')}</p>
          ) : null}
          {!isLoading && !products.length ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">{t('memberDetails.productLevels.none')}</p>
          ) : null}
          {!isLoading && products.length ? (
            <ul className="max-h-72 overflow-y-auto p-1">
              {products.map(product => {
                const checked = picked.includes(product.id);
                return (
                  <li key={product.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      onClick={() => toggle(product.id, !checked)}
                      disabled={isPending}
                      className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Checkbox checked={checked} aria-hidden tabIndex={-1} />
                      <span className="min-w-0 flex-1 truncate">{product.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            {t('memberDetails.productLevels.cancel')}
          </Button>
          <Button type="button" onClick={() => onConfirm(picked)} disabled={isPending || isLoading}>
            {picked.length
              ? t('memberDetails.productLevels.confirm', { count: picked.length })
              : t('memberDetails.productLevels.profileOnly')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductLevelsDialog;
